import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useApp } from 'contexts/app';
import { useWorkSpace } from 'contexts/workspace';
import StoreSettingService from 'services/store_setting';

const HomePageSettingContext = createContext();
const service = new StoreSettingService();

const createBlock = (type) => {
    const id = `_${Math.random()}`;

    if (type === 'product') {
        return {
            id,
            type: 'product',
            setting: {
                title: 'Sản phẩm',
                display: 'list',
                products: {
                    method: 'list',
                    list: [],
                    tag: [],
                    category: [],
                },
            },
        };
    }

    if (type === 'image') {
        return {
            id,
            type: 'image',
            setting: {
                title: 'Hình ảnh',
                show_title: false,
                image: null,
            },
        };
    }

    if (type === 'multi_image') {
        return {
            id,
            type: 'multi_image',
            setting: {
                title: 'Hình ảnh',
                show_title: false,
                display: 'carousel',
                images: [],
            },
        };
    }

    if (type === 'product_all') {
        return {
            id,
            type: 'product_all',
            setting: {
                title: 'Tất cả sản phẩm',
                show_title: false,
            },
        };
    }

    if (type === 'product_collections') {
        return {
            id,
            type: 'product_collections',
            setting: {
                title: 'Bộ sưu tập',
                show_title: true,
                collection_ids: [],
                display: 'list',
                slide_config: {
                    autoplay: true,
                },
                common_config: {
                    round_image: false,
                    show_name: true,
                },
            },
        };
    }

    return null;
};

const HomePageSettingProvider = ({ children }) => {
    const [editItem, setEditItem] = useState(null);

    const showEditItem = (id) => {
        setEditItem(id);
    };

    const hideEditItem = () => {
        setEditItem(null);
    };

    return (
        <HomePageSettingContext.Provider value={{ editItem, showEditItem, hideEditItem }}>
            {children}
        </HomePageSettingContext.Provider>
    );
};

const useHomePageSetting = () => {
    return useContext(HomePageSettingContext);
};

const useHomePageSettingScreen = () => {
    const { showLoading, showNotify, message } = useApp();
    const { workspace } = useWorkSpace();
    const { showEditItem } = useHomePageSetting();
    const [blocks, setBlocks] = useState([]);
    const [display, setDisplay] = useState('default');
    const [showAdd, setShowAdd] = useState(false);
    const [pageUrl, setPageUrl] = useState('');
    const previewTimerRef = useRef(null);

    const addBlockOptions = [
        { key: 'product', title: 'Sản phẩm', description: 'Danh sách sản phẩm.' },
        { key: 'image', title: '1 Hình ảnh', description: 'Thêm hình ảnh' },
        { key: 'multi_image', title: 'Slide ảnh', description: 'Thêm nhiều hình ảnh' },
        { key: 'product_all', title: 'Toàn bộ sản phẩm', description: 'Danh sách toàn bộ sản phẩm' },
        { key: 'product_collections', title: 'ProductCollections', description: 'Hiển thị hình ảnh các collection' },
    ];

    const displayOptions = [
        { label: 'Mặc định', value: 'default' },
        { label: 'Tuỳ chỉnh', value: 'custom' },
    ];

    const loadData = async () => {
        const setting = await service.getHomePageSetting();
        setDisplay(setting?.display || 'default');
        setBlocks(Array.isArray(setting?.blocks) ? setting.blocks : []);
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        setPageUrl(workspace?.shopDomainUrl || '');
    }, [workspace?.shopDomainUrl]);

    useEffect(() => {
        if (previewTimerRef.current) {
            clearTimeout(previewTimerRef.current);
        }

        previewTimerRef.current = setTimeout(() => {
            const iframe = document.getElementById('previewIfame');
            if (!iframe?.contentWindow) {
                return;
            }

            iframe.contentWindow.postMessage(
                {
                    sender: 'get_page_button2',
                    message: {
                        action: 'home_page_changed',
                        data: {
                            display,
                            blocks,
                        },
                    },
                },
                '*'
            );
        }, 120);

        return () => {
            if (previewTimerRef.current) {
                clearTimeout(previewTimerRef.current);
            }
        };
    }, [blocks, display]);

    const onSave = async () => {
        showLoading(true);
        try {
            await service.saveHomePageSetting({ display, blocks });
            await loadData();
            showNotify('Đã cập nhật');
        } catch (error) {
            message.error('Không thể nhật');
        }
        showLoading(false);
    };

    const onBlocksChange = (values) => {
        setBlocks(values);
    };

    const onAddBlock = (type) => {
        const newBlock = createBlock(type);
        if (!newBlock) {
            return;
        }

        setBlocks((prev) => [...prev, newBlock]);
        setShowAdd(false);

        if (type !== 'product') {
            showEditItem(newBlock.id);
        }
    };

    return {
        blocks,
        display,
        showAdd,
        pageUrl,
        displayOptions,
        addBlockOptions,
        setDisplay,
        setShowAdd,
        onSave,
        onBlocksChange,
        onAddBlock,
    };
};

export { HomePageSettingProvider, useHomePageSetting, useHomePageSettingScreen };
